import { useEffect, useRef } from 'react';

import type { CanonicalEntity } from '@/shared/api/types';
import { useBundleSelector } from '@/shared/api/useBundleSelector';
import { useViewerStore } from '@/shared/store/viewer-store';

import { stepSelectionUp } from './selectionAxis';

/*
 * `Esc` — ЭТО ШАГ ВВЕРХ ПО ОСИ, А НЕ ВЫХОД ИЗ РАБОТЫ.
 *
 * Было (app/App.tsx, ветка `e.key === 'Escape'`): `v.clearSelection()` при любом
 * выделении. Одна клавиша, одна ветка, никакого понятия об уровне — человек,
 * зашедший в деталь шкафа, нажимал `Esc` и терял выделение целиком, после чего
 * искал тот же шкаф заново в дереве слева.
 *
 * Стало: `Esc` поднимает выделение на уровень выше по оси
 * «комната → композиция → модуль → деталь», и снимает его ТОЛЬКО с верхнего
 * уровня. Правило целиком живёт в `selectionAxis.ts`; здесь только клавиша и
 * доступ к бандлу.
 *
 * Почему хук, а не ещё одна ветка в общем обработчике `App.tsx`: подъёму нужен
 * список сущностей, а общий обработчик о бандле не знает вовсе и знать не
 * должен — он про клавиши приложения, а это про модель.
 */
export function useSelectionAxisKeys(): void {
  const activeModelId = useViewerStore((state) => state.activeModelId);
  const { data: entities } = useBundleSelector(
    activeModelId,
    (bundle) => bundle?.canonicalScene?.entities ?? [],
  );
  /* Обработчик регистрируется ОДИН раз: перевешивать слушателя на каждую правку
     бандла — это терять нажатие, попавшее между снятием и установкой. */
  const entitiesRef = useRef<readonly CanonicalEntity[]>([]);
  entitiesRef.current = entities ?? [];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      const viewer = useViewerStore.getState();
      const ids = viewer.selectedIds.length
        ? viewer.selectedIds
        : viewer.selectedId ? [viewer.selectedId] : [];
      if (!ids.length) return;

      const byId = new Map(entitiesRef.current.map((entity) => [String(entity.id), entity]));
      const step = stepSelectionUp(ids, byId);
      if (step.kind === 'none') return;

      event.preventDefault();
      if (step.kind === 'clear') {
        viewer.clearSelection();
        return;
      }
      viewer.setSelectedMany([...step.ids]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
